import { Component, OnDestroy, OnInit } from '@angular/core';
import { getPercentageChange, isValidNumber } from 'app/functions/functions';
import { StockService } from 'app/sections/stocks/services/stock.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Ticker } from 'app/interfaces/ticker';
import { SocketTicker } from 'app/interfaces/socket-ticker';
declare let echarts;

@Component({
  selector: 'app-market-map',
  templateUrl: './market-map.component.html',
  styleUrls: ['./market-map.component.scss']
})
export class MarketMapComponent implements OnInit, OnDestroy {
  private _unsubscribeAll: Subject<any>;
  public treeMapChartOption: BehaviorSubject<any> = new BehaviorSubject(null);
  private mapData: any = null;
  constructor(
    private stockService: StockService) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    /* if tickerlist null */
    if (!this.stockService.tickersList.getValue() && !this.mapData) {
      this.stockService.getAllTickerList().pipe(takeUntil(this._unsubscribeAll)).subscribe((tickers: Ticker[]) => {
        /* set percentageChange */
        tickers.map((tick) => {
          tick.price = Number(tick.price.toFixed(2));
          tick.percentageChange = Number((((tick.price / tick.previousClose) - 1) * 100).toFixed(2));
          return tick;
        });

        this.stockService.tickersList.next(tickers);

        this.stockService.getIndustryMap().subscribe((map: any) => {
          /* update industry */
          this.mapData = map.data;
          this.updateIndustry().then(() => {
            this.updateMap();
          });
        });
      });
    } else {
      this.stockService.getIndustryMap().subscribe((map: any) => {
        /* update industry */
        this.mapData = map.data;
        this.updateIndustry().then(() => {
          this.updateMap();
        });
      });
    }
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  treeMap(data) {
    let visualMin = -100;
    let visualMax = 100;
    let option = {
      tooltip: {
        formatter: (info) => {
          let value = info.value;

          let amount = value[0];
          amount = isValidNumber(amount)
            ? echarts.format.addCommas((amount / 1000000).toFixed(0))
            : '-';

          let amount2011 = value[1];
          amount2011 = isValidNumber(amount2011)
            ? echarts.format.addCommas(amount2011)
            : '-';

          let change = value[2];
          change = isValidNumber(change)
            ? ((change).toFixed(2)) + '%'
            : '-';

          return [
            '<div class="tooltip-title">' + echarts.format.encodeHTML(info.name) + '</div>',
            'Market Cap (M): &nbsp;&nbsp; $' + amount + '<br>',
            'Price: &nbsp;&nbsp; $' + amount2011 + '<br>',
            'Net Change: &nbsp;&nbsp;' + change
          ].join('');
        }
      },
      label: {
        position: 'insideRight',
        formatter: function (params) {
          let arr = [
            '{name|' + params.name + '}',
            `{change|${Number(params.value[2]).toFixed(2)}%}`
            // '{budget|$ ' +
            //   echarts.format.addCommas(params.value[0]) +
            //   '} {label|budget}'
          ];
          return arr.join('\n');
        },
        rich: {
          name: {
            fontSize: 20,
            color: '#fff',
            align: 'center'
          },
          change: {
            fontSize: 10,
            color: '#fff',
            align: 'center'
          }
        }
      },
      series: [{
        width: '100%',
        height: '100%',
        name: 'ALL',

        /* for disbale zoom */
        roam: 'move',

        // top: 80,
        type: 'treemap',
        label: {
          show: false,
          position: 'insideRight',
          formatter: "{b}",
          normal: {
            textStyle: {
              ellipsis: true
            }
          }
        },
        itemStyle: {
          normal: {
            borderColor: 'black'
          }
        },
        visualMin: visualMin,
        visualMax: visualMax,
        visualDimension: 3,
        levels: [
          {
            itemStyle: {
              borderWidth: 3,
              borderColor: '#333',
              gapWidth: 3
            }
          },
          {
            // color: ['#f63538', '#bf4045', '#8b444e', '#414554', '#35764e', '#2f9e4f', '#30cc5a'],
            color: ['#942e38', '#aaa', '#269f3c'],
            colorMappingBy: 'value',
            itemStyle: {
              gapWidth: 1
            }
          }
        ],
        data: data
      }]
    }
    return option;
  }

  updateIndustry() {
    return new Promise((resolve) => {
      this.stockService.tickersListObservable.pipe(takeUntil(this._unsubscribeAll)).subscribe((tickers: Ticker[]) => {
        if (tickers) {
          tickers.map((tic) => {
            this.updateIndustryMapFindTicker(tic, this.mapData);
          });
          this.convertData(this.mapData).then(() => {
            this.treeMapChartOption.next(this.treeMap(this.mapData));
            resolve(true);
          });
        }
      })
    })
  }

  updateIndustryMapFindTicker(tic: Ticker, mapData: any) {
    if (mapData) {
      mapData.map(industryMap => {
        if (industryMap.children) {
          industryMap.children.map(child => {
            if (child.name === tic.ticker) {
              if (isValidNumber(tic.price) && isValidNumber(tic.previousClose)) {
                child.value[1] = Number(tic.price.toFixed(2));
                child.value[2] = getPercentageChange(tic.price, tic.previousClose);
              }
            }
          });
        }
      });
      return mapData;
    }
  }

  convertData(originList) {
    return new Promise((resolve) => {
      let visualMin = -100;
      let visualMax = 100;
      let visualMinBound = -40;
      let visualMaxBound = 40;
      let min = Infinity;
      let max = -Infinity;

      for (let i = 0; i < originList.length; i++) {
        let node = originList[i];
        if (node) {
          let value = node.value;
          value[2] != null && value[2] < min && (min = value[2]);
          value[2] != null && value[2] > max && (max = value[2]);
        }
      }

      for (let i = 0; i < originList.length; i++) {
        let node = originList[i];
        if (node) {
          let value = node.value;

          // Scale value for visual effect
          if (value[2] != null && value[2] > 0) {
            value[3] = echarts.number.linearMap(
              value[2], [0, max], [visualMaxBound, visualMax], true
            );
          }
          else if (value[2] != null && value[2] < 0) {
            value[3] = echarts.number.linearMap(
              value[2], [min, 0], [visualMin, visualMinBound], true
            );
          }
          else {
            value[3] = 0;
          }

          if (!isFinite(value[3])) {
            value[3] = 0;
          }

          if (node.children) {
            this.convertData(node.children);
          }
        }
      }
      resolve(true);
    });
  }

  updateMap() {
    this.stockService.getSocketTicker().pipe(takeUntil(this._unsubscribeAll)).subscribe((tic: SocketTicker) => {
      let tickers: Ticker[] = this.stockService.tickersList.getValue();
      let index = tickers.map(t => t.ticker.toLowerCase()).indexOf(tic.tickerName.toLowerCase());
      if (index >= 0) {
        tickers[index].price = Number(tic.price.toFixed(2));
        tickers[index].percentageChange = Number((((Number(tic.price) / Number(tickers[index].previousClose)) - 1) * 100).toFixed(2));
        this.stockService.tickersList.next(tickers);
      }
    });
  }

}
